import { useMemo } from 'react';
import { Alert, Button, Flex, Space, Typography } from 'antd';
import { useAreaBoundaryPickerMap } from '~/app/hooks/site/useAreaBoundaryPickerMap';
import type { PolygonPerimeter } from '@evimo/types';

const { Paragraph, Text } = Typography;

type Props = {
  addressQuery: string | null;
  perimeter?: PolygonPerimeter | null;
  overlayPerimeter?: PolygonPerimeter | null;
  referenceCoordinates?: { latitude: number; longitude: number } | null;
  disabled: boolean;
  selectionRequiredText: string;
  instructionsText: string;
  currentPerimeterText: string;
  missingKeyText: string;
  geocodeFailedText: string;
  loadFailedText: string;
  locatingText: string;
  clearText: string;
  onPerimeterChangeAction: (perimeter: PolygonPerimeter | null) => void;
};

export function AreaBoundaryPicker({
  addressQuery,
  perimeter,
  overlayPerimeter,
  referenceCoordinates,
  disabled,
  selectionRequiredText,
  instructionsText,
  currentPerimeterText,
  missingKeyText,
  geocodeFailedText,
  loadFailedText,
  locatingText,
  clearText,
  onPerimeterChangeAction,
}: Props) {
  const pointCount = useMemo(() => perimeter?.length ?? 0, [perimeter]);

  const { errorMessage, isLocating, mapContainerRef } =
    useAreaBoundaryPickerMap({
      addressQuery,
      selectedPerimeter: perimeter ?? null,
      overlayPerimeter,
      referenceCoordinates,
      disabled,
      missingKeyText,
      geocodeFailedText,
      loadFailedText,
      onPerimeterChangeAction,
    });

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Flex justify="space-between" align="flex-start" gap={12} wrap>
        <div>
          <Paragraph type="secondary" style={{ marginBottom: 4 }}>
            {instructionsText}
          </Paragraph>
          <Text type="secondary">
            {currentPerimeterText}: {pointCount}
          </Text>
        </div>

        <Button
          disabled={pointCount === 0}
          onClick={() => onPerimeterChangeAction(null)}
        >
          {clearText}
        </Button>
      </Flex>

      {disabled ? (
        <Alert type="info" showIcon message={selectionRequiredText} />
      ) : errorMessage ? (
        <Alert type="warning" showIcon message={errorMessage} />
      ) : null}

      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: 400,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#f8fafc',
          border: '1px solid #d9e4f0',
          display: disabled ? 'none' : 'block',
        }}
      />

      {!disabled && isLocating ? (
        <Text type="secondary">{locatingText}</Text>
      ) : null}
    </Space>
  );
}
